import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import BillingReportRepository from '@/repositories/BillingReportRepository';
import JurisdictionRepository, { type Jurisdiction } from '@/repositories/JurisdictionRepository';
import Accordion from '@/ui/components/Accordion';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Datepicker from '@/ui/components/Form/Datepicker';
import InputField from '@/ui/components/Form/InputField';
import InputGroup from '@/ui/components/Form/InputGroup';
import { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading';
import PageHeaderBar from '@/ui/components/PageHeaderBar';
import Spinner from '@/ui/components/Spinner';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import { BillingReportFormValues } from './types';

interface BillingReportFormProps {
  reportId?: string;
  onSuccess?: () => void;
}

// Returns the UTC offset string (e.g. "-08:00" or "-07:00") for US/Pacific
// on the given YYYY-MM-DD date, respecting daylight saving time.
function pacificOffset(dateStr: string): string {
  const date = new Date(`${dateStr}T12:00:00`); // noon avoids DST boundary edge cases
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    timeZoneName: 'shortOffset'
  });
  const parts = formatter.formatToParts(date);
  const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT-8';
  // Handles both "GMT-7" and "GMT-07:00" runtime formats
  const match = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return '-08:00';
  const sign = match[1];
  const hours = String(parseInt(match[2], 10)).padStart(2, '0');
  const minutes = match[3] ?? '00';
  return `${sign}${hours}:${minutes}`;
}

const BillingReportForm: React.FC<BillingReportFormProps> = ({
  reportId,
  onSuccess
}: BillingReportFormProps) => {
  const { addMessage } = useContext(AppContext);
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch
  } = useForm<BillingReportFormValues>({
    defaultValues: {
      range_start: '',
      range_end: '',
      person_id: ''
    }
  });

  const jurisdictionValue = watch('jurisdiction');

  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([]);
  const [isLoadingReport, setIsLoadingReport] = useState(!!reportId);
  const billingReportRepository = BillingReportRepository();
  const jurisdictionRepository = JurisdictionRepository();

  useEffect(() => {
    const loadJurisdictions = async () => {
      try {
        const data = await jurisdictionRepository.getJurisdictions();
        setJurisdictions(data);
      } catch (error) {
        console.error('Failed to load jurisdictions:', error);
        addMessage({
          level: 'error',
          text: 'Failed to load jurisdictions'
        });
      }
    };

    loadJurisdictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only load jurisdictions once on mount

  useEffect(() => {
    const loadReport = async () => {
      if (!reportId) return;

      setIsLoadingReport(true);
      const { data, isError, errorMessage } =
        await billingReportRepository.getBillingReport(reportId);

      if (isError || !data) {
        addMessage({
          level: 'error',
          text: errorMessage || 'Failed to load billing report'
        });
        setIsLoadingReport(false);
        return;
      }

      reset({
        range_start: data.range_start.split('T')[0],
        range_end: data.range_end.split('T')[0],
        jurisdiction: data.jurisdiction || null,
        person_id: data.person_id || ''
      });
      setIsLoadingReport(false);
    };

    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]); // Only reload when reportId changes

  const mutation = useMutation({
    mutationFn: async (values: BillingReportFormValues) => {
      const payload = {
        id: reportId,
        range_start: `${values.range_start}T00:00:00${pacificOffset(values.range_start)}`,
        range_end: `${values.range_end}T23:59:59${pacificOffset(values.range_end)}`,
        jurisdiction: values.jurisdiction || null,
        person_id: values.person_id || undefined
      };

      if (reportId) {
        const response = await billingReportRepository.updateBillingReport(payload);
        if (response.isError) throw response.error;
        return response.data;
      } else {
        const response = await billingReportRepository.createBillingReport(payload);
        if (response.isError) throw response.error;
        return response.data;
      }
    },
    onSuccess: () => {
      addMessage({
        level: 'success',
        text: reportId
          ? 'Billing report updated successfully'
          : 'Billing report created successfully'
      });

      if (onSuccess) {
        onSuccess();
      } else {
        // Redirect to the reports list where backend will auto-generate
        navigate(`/billing/reports/`);
      }
    },
    onError: (error: AxiosError) => {
      // Handle field-specific errors from API response
      if (error.response?.data && typeof error.response.data === 'object') {
        const errorData = error.response.data as Record<string, string | string[]>;
        Object.entries(errorData).forEach(([field, message]) => {
          if (field !== 'non_field_errors') {
            setError(field as keyof BillingReportFormValues, {
              type: 'server',
              message: Array.isArray(message) ? message.join(', ') : String(message)
            });
          }
        });
      }

      // Show general error message
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const onSubmit = (values: BillingReportFormValues) => {
    mutation.mutate(values);
  };

  const jurisdictionOptions: OptionType<string>[] = jurisdictions.map((jurisdiction) => ({
    label: jurisdiction.name,
    value: jurisdiction.id.toString()
  }));

  const selectedJurisdiction = jurisdictionValue
    ? jurisdictionOptions.find((opt) => opt.value === jurisdictionValue.toString()) || null
    : null;

  if (isLoadingReport) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <PageHeaderBar>
        <Heading level={1}>{reportId ? 'Edit' : 'New'} Billing Report</Heading>
      </PageHeaderBar>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <InputGroup>
            <Controller
              control={control}
              name="range_start"
              rules={{ required: 'Start date is required' }}
              render={({ field }) => (
                <Datepicker
                  label="Range Start"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
            <FieldError message={errors.range_start?.message} />
            <p className="text-sm text-gray-500">The start date is inclusive.</p>
          </InputGroup>

          <InputGroup>
            <Controller
              control={control}
              name="range_end"
              rules={{ required: 'End date is required' }}
              render={({ field }) => (
                <Datepicker
                  label="Range End"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
            <FieldError message={errors.range_end?.message} />
            <p className="text-sm text-gray-500">The end date is inclusive.</p>
          </InputGroup>

          <InputGroup>
            <Controller
              control={control}
              name="jurisdiction"
              render={({ field }) => (
                <AutocompleteSingleSelect
                  label="Jurisdiction"
                  placeholder='Optional - select a jurisdiction to filter by (e.g. "California")'
                  clearable={true}
                  onChange={(value: OptionType<string> | null) => {
                    field.onChange(value?.value || null);
                  }}
                  options={jurisdictionOptions}
                  selectedOption={selectedJurisdiction}
                />
              )}
            />
            <FieldError message={errors.jurisdiction?.message} />
          </InputGroup>

          <Accordion buttonText={'Advanced Options'}>
            <p className="mb-4 text-sm text-gray-500">
              These options are typically only needed for debugging or specific use cases.
            </p>
            <InputGroup>
              <Controller
                control={control}
                name="person_id"
                render={({ field }) => (
                  <InputField
                    id="person_id"
                    label="Person ID (Optional)"
                    placeholder="Person ID"
                    type="text"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              <FieldError message={errors.person_id?.message} />
              <p className="text-sm text-gray-500">Useful for debugging.</p>
            </InputGroup>
          </Accordion>

          <div className="flex gap-4">
            <Button color="blue" disabled={mutation.isPending} type="submit" className="flex-1">
              {mutation.isPending ? 'Saving...' : 'Save'}
            </Button>
            <Button color="white" onClick={() => navigate(-1)} type="button">
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
};

export default BillingReportForm;
